/* eslint-disable @nx/enforce-module-boundaries */
import {
  CalendarIcon,
  CausesIcon,
  GlobeIcon,
  InterestIcon,
  LinkIcon,
  LocationIcon,
  MailIcon,
  TelephoneIcon,
  UnreadMessageIcon,
} from '@mybridge/icons';
import { UserProfileIcon } from '@mybridge/icons/chat-icons';
import {
  HStack,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
  Box,
} from '@mybridge/ui';
import { useContext } from 'react';
import { LANG_CHOICES } from 'v4/lib/data';
import { PublicProfileContext } from '../context';
import moment from 'moment';
import { getUserLocation } from 'v4/lib/commons';
import { MyNetworkContext } from 'v4/components/network/context';
import _ from 'lodash';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { MobileView, isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';

export const MyProfilePersonalInfo = ({ displayCard, ...props }) => {
  const { publicProfile } = useContext(PublicProfileContext);
  const { isFriend } = useContext(MyNetworkContext);
  const { t } = useTranslator();

  const {
    interested_in,
    causes_you_care,
    website,
    social_links,
    email,
    mobile_number,
    language,
    dob,
    city,
    location,
    gender,
    pronouns,
    iso_code,
  } = publicProfile ?? {};

  const emailValue = _.get(publicProfile, 'field_audience.email');
  const phoneValue = _.get(publicProfile, 'field_audience.mobile_number');
  const languageValue = _.get(publicProfile, 'field_audience.lang');
  const locationValue = _.get(publicProfile, 'field_audience.location');
  const dobMonthValue = _.get(publicProfile, 'field_audience.dob_month');
  const dobYearValue = _.get(publicProfile, 'field_audience.dob_year');
  const interestedInValue = _.get(publicProfile, 'field_audience.interested_in');
  const causesYouCareValue = _.get(publicProfile, 'field_audience.causes_you_care');
  const websiteValue = _.get(publicProfile, 'field_audience.website');
  const socialLinksValue = _.get(publicProfile, 'field_audience.social_links');
  const genderValue = _.get(publicProfile, 'field_audience.gender');
  const pronounsValue = _.get(publicProfile, 'field_audience.pronouns');

  // 🧠 Normalize website & social links to always be arrays
  const safeWebsites = Array.isArray(website)
    ? website.filter(Boolean)
    : website
    ? [website]
    : [];

  const safeSocialLinks = Array.isArray(social_links)
    ? social_links.filter(Boolean)
    : social_links
    ? [social_links]
    : [];

  // Helper: create a valid URL
  const toValidUrl = (url) => {
    if (typeof url !== 'string' || !url.trim()) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  // Check if this is the user's own profile
  const { userProfileInfo: currentUser } = useSelector((state) => state.userProfile);
  const isOwnProfile = publicProfile?.id === currentUser?.id;

  // Helper functions to check if fields should be visible based on audience settings
  const shouldShowField = (audienceValue) => {
    return (
      isOwnProfile || // Own profile
      audienceValue === undefined || // No audience setting (default to visible)
      audienceValue === 1 || // Public
      (audienceValue === 2 && isFriend(publicProfile)) // Connections only and is a connection
    );
  };

  // Check if fields should be visible
  const shouldShowEmail = shouldShowField(emailValue);
  const shouldShowPhone = shouldShowField(phoneValue);
  const shouldShowLanguage = shouldShowField(languageValue);
  const shouldShowLocation = shouldShowField(locationValue);
  const shouldShowDobMonth = shouldShowField(dobMonthValue) && dobMonthValue !== 3;
  const shouldShowDobYear = shouldShowField(dobYearValue) && dobYearValue !== 3;
  const shouldShowInterestedIn = shouldShowField(interestedInValue);
  const shouldShowCauses = shouldShowField(causesYouCareValue);
  const shouldShowWebsite = shouldShowField(websiteValue);
  const shouldShowSocialLinks = shouldShowField(socialLinksValue);
  const shouldShowGender = shouldShowField(genderValue);
  const shouldShowPronouns = shouldShowField(pronounsValue);

  return (
    <>
      {displayCard ? (
        <Stack>
          <Stack spacing={6}>
            {/* Interests */}
            {interested_in && shouldShowInterestedIn && (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <InterestIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {interested_in}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.interests') || 'Interests'}
                  </Text>
                </VStack>
              </HStack>
            )}

            {/* Causes */}
            {causes_you_care && shouldShowCauses && (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <CausesIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {causes_you_care}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.causesYouCareAbout') ||
                      'Causes you care about'}
                  </Text>
                </VStack>
              </HStack>
            )}

            {/* Website */}
            {safeWebsites.length > 0 && shouldShowWebsite && (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <GlobeIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {safeWebsites.map((site, i) => (
                      <Link
                        key={i}
                        href={toValidUrl(site)}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="block"
                      >
                        {site}
                      </Link>
                    ))}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.website') || 'Website'}
                  </Text>
                </VStack>
              </HStack>
            )}

            {/* Social Links */}
            {safeSocialLinks.length > 0 && shouldShowSocialLinks && (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <LinkIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {safeSocialLinks.map((link, i) => (
                      <Link
                        key={i}
                        href={toValidUrl(link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="block"
                      >
                        {link}
                      </Link>
                    ))}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.socialLinks') || 'Social Links'}
                  </Text>
                </VStack>
              </HStack>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack>
          <Stack flex={1}>
            <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
              {isMobile ? ('Profile details') : (t('publicProfile.profile') || 'Profile')}
            </Heading>
          </Stack>

          <Stack spacing={6} pl={[3, '']}>
            {/* Email */}
            {email && shouldShowEmail ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <MailIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {email}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.email') || 'Email'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowEmail && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <MailIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No email to show
                </Text>
              </HStack>
            ) : null}

            {/* Mobile */}
            {mobile_number && shouldShowPhone ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <TelephoneIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {iso_code} {mobile_number}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.mobileNumber') || 'Mobile Number'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowPhone && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <TelephoneIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No phone number to show
                </Text>
              </HStack>
            ) : null}

            {/* Languages */}
            {language?.length && shouldShowLanguage ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <GlobeIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {language.map((lang, i) => (
                      <Box as="span" key={i}>
                        {lang}
                        {i < language.length - 1 && ', '}
                      </Box>
                    ))}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.languages') || 'Languages'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowLanguage && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <GlobeIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No languages to show
                </Text>
              </HStack>
            ) : null}

            {/* DOB */}
            {dob ? (
              <>
                {shouldShowDobMonth && (
                  <HStack alignItems="flex-start">
                    <Box mt="3px">
                      <CalendarIcon width={20} height={20} color="#868585" />
                    </Box>
                    <VStack alignItems="flex-start" gap={0}>
                      <Text fontSize="15px" color="#868585">
                        {moment(dob).format('MMMM')}
                      </Text>
                      <Text fontSize="sm" color="#868585">
                        {t('publicProfile.birthMonth') || 'Birth Month'}
                      </Text>
                    </VStack>
                  </HStack>
                )}

                {shouldShowDobYear && (
                  <HStack alignItems="flex-start">
                    <Box mt="3px">
                      <CalendarIcon width={20} height={20} color="#868585" />
                    </Box>
                    <VStack alignItems="flex-start" gap={0}>
                      <Text fontSize="15px" color="#868585">
                        {moment(dob).format('YYYY')}
                      </Text>
                      <Text fontSize="sm" color="#868585">
                        {t('profileDetails.birthYear') || 'Birth Year'}
                      </Text>
                    </VStack>
                  </HStack>
                )}
              </>
            ) : (!shouldShowDobMonth || !shouldShowDobYear) && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <CalendarIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No birth date to show
                </Text>
              </HStack>
            ) : null}

            {/* Location */}
            {(city && location) && shouldShowLocation ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <LocationIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {location || getUserLocation(publicProfile)}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.location') || 'Location'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowLocation && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <LocationIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No location to show
                </Text>
              </HStack>
            ) : null}

            {/* Interests */}
            {interested_in && shouldShowInterestedIn ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <InterestIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {interested_in}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.interests') || 'Interests'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowInterestedIn && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <InterestIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No interests to show
                </Text>
              </HStack>
            ) : null}

            {/* Causes */}
            {causes_you_care && shouldShowCauses ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <CausesIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {causes_you_care}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.causesYouCareAbout') ||
                      'Causes you care about'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowCauses && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <CausesIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No causes to show
                </Text>
              </HStack>
            ) : null}

            {/* Website */}
            {safeWebsites.length > 0 && shouldShowWebsite ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <GlobeIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {safeWebsites.map((site, i) => (
                      <Link
                        key={i}
                        href={toValidUrl(site)}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="block"
                      >
                        {site}
                      </Link>
                    ))}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.website') || 'Website'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowWebsite && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <GlobeIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No website to show
                </Text>
              </HStack>
            ) : null}

            {/* Social Links */}
            {safeSocialLinks.length > 0 && shouldShowSocialLinks ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <LinkIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {safeSocialLinks.map((link, i) => (
                      <Link
                        key={i}
                        href={toValidUrl(link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="block"
                      >
                        {link}
                      </Link>
                    ))}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.socialLinks') || 'Social Links'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowSocialLinks && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <LinkIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No social links to show
                </Text>
              </HStack>
            ) : null}

            {/* Gender */}
            {gender && shouldShowGender ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                   <UserProfileIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {gender}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.gender') || 'Gender'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowGender && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <UserProfileIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No gender to show
                </Text>
              </HStack>
            ) : null}

            {/* Pronouns */}
            {pronouns && shouldShowPronouns ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <UnreadMessageIcon width={20} height={20} color="#868585" />
                </Box>
                <VStack alignItems="flex-start" gap={0}>
                  <Text fontSize="15px" color="#868585">
                    {pronouns}
                  </Text>
                  <Text fontSize="sm" color="#868585">
                    {t('profileDetails.pronouns') || 'Pronouns'}
                  </Text>
                </VStack>
              </HStack>
            ) : !shouldShowPronouns && !isOwnProfile ? (
              <HStack alignItems="flex-start">
                <Box mt="3px">
                  <UnreadMessageIcon width={20} height={20} color="#868585" opacity={0.6} />
                </Box>
                <Text fontSize="15px" color="#868585" opacity={0.6}>
                  No pronouns to show
                </Text>
              </HStack>
            ) : null}
          </Stack>
        </Stack>
      )}
    </>
  );
};
